import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs, useFlags } from 'wsm-common-data';
import useEventBus from './useEventBus';
import {
	setPaymentsCalcResponse,
} from '../features/paymentsInfo/paymentsInfoSlice';
import {
	addPaymentTimeoutToNewRelic,
	addTotalTimeToNewRelic,
	addPaymentRenderToNewRelic,
	addPaymentMismatchToNewRelic,
	comparePaymentData,
	startTimeMarker,
	endTimeMarker,
	getPerformanceMeasure
} from '../helpers/logging-client';
import { ESNTIAL_PAYMENT_ERROR_MESSAGE } from '../constants';
import useErrorHandler from './useErrorHandler';
import {
	extractVehicleUUID,
	getCachedPaymentData
} from '../helpers/payment-cache-utils';
import {
	DEFAULT_PAYMENT_TIMEOUT_MS,
	CACHE_PRELOAD_FLAG,
	TIMEOUT_FLAG,
	TEST_FUTURE_FLAG
} from './usePaymentData/constants';
import {
	processPaymentObjectUpdate,
	hasAnyValidPaymentInRawData
} from './usePaymentData/processors/paymentObjectProcessor';

const usePaymentData = ({ enabled = true } = {}) => {
	const { enableIncentives } = usePrefs();
	const dispatch = useDispatch();
	const supportedDealTypesRef = useRef([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [udsPayments, setUdsPayments] = useState(null);
	const [paymentsData, setPaymentsData] = useState(null);
	const [hasReceivedData, setHasReceivedData] = useState(false); // Track if we've received any payment data
	const [eventBus, isReady] = useEventBus();
	const paymentsRef = useRef(udsPayments);
	const errorTimeoutRef = useRef(null);
	const cachedPaymentDataRef = useRef(null);
	const hasRenderedCachedRef = useRef(false);
	const pendingPaymentRef = useRef(null);
	const isInitialCalcDoneRef = useRef(false);
	const initialEventCountRef = useRef(0);
	const flags = useFlags();
	const paymentTimeout =
		flags[TIMEOUT_FLAG] || DEFAULT_PAYMENT_TIMEOUT_MS;

	const hideOnEsntialPaymentError = useErrorHandler(
		setHasError,
		setIsLoading,
		ESNTIAL_PAYMENT_ERROR_MESSAGE
	);

	const convertLegacyCacheToPaymentObject = (legacyData) => {
		try {
			const rateKey = legacyData?.rate ? 'rate' : 'rates';
			if (!legacyData[rateKey]) {
				logger.warn('WS-TRACK-MY-OFFER: No rate/rates key in cached data');
				return null;
			}

			const creditRatingKey = Object.keys(legacyData[rateKey])[0];
			if (!creditRatingKey) {
				logger.warn('WS-TRACK-MY-OFFER: No credit rating in cached data');
				return null;
			}

			const ratesData = legacyData[rateKey][creditRatingKey];
			if (!ratesData || typeof ratesData !== 'object') {
				logger.warn('WS-TRACK-MY-OFFER: No payment types found for credit rating', {
					creditRatingKey,
					ratesData
				});
				return null;
			}

			const paymentObjects = [];

			// Convert finance/payment offers
			if (ratesData.paymentOffers || ratesData.nationalOffers) {
				const financeKey = ratesData.nationalOffers ? 'nationalOffers' : 'paymentOffers';
				const financeTerms = ratesData[financeKey];
				const termKeys = Object.keys(financeTerms || {});

				if (termKeys.length > 0 && !legacyData.isFinanceErrorState) {
					const term = termKeys[0];
					const termData = financeTerms[term]?.[0];
					if (termData?.payment > 0) {
						paymentObjects.push({
							paymentLabel: 'finance',
							payment: termData.payment,
							isPaymentError: false,
							loanTerm: term !== '-1' ? parseInt(term, 10) : null,
							interestRate: termData.new || termData.used,
							downPayment: termData.downPayment,
							incentiveIds: termData.incentiveIds || []
						});
					}
				}
			}

			// Convert lease offers
			if (ratesData.leaseOffers && !legacyData.isLeaseErrorState) {
				const leaseTerms = ratesData.leaseOffers;
				const termKeys = Object.keys(leaseTerms || {});

				if (termKeys.length > 0) {
					const term = termKeys[0];
					const termData = leaseTerms[term]?.[0];
					if (termData?.payment > 0) {
						paymentObjects.push({
							paymentLabel: 'lease',
							payment: termData.payment,
							isPaymentError: false,
							leaseTerm: term !== '-1' ? parseInt(term, 10) : null,
							annualMiles: termData.annualMiles,
							downPayment: termData.downPayment,
							incentiveIds: termData.incentiveIds || []
						});
					}
				}
			}

			// Convert cash offers
			if (ratesData.cashOffers) {
				const cashData = ratesData.cashOffers?.['-1']?.[0];
				const cashAmount = cashData?.cashAmount || cashData?.payment || cashData?.pricingDetails?.salePrice;

				if (cashAmount > 0) {
					paymentObjects.push({
						paymentLabel: 'cash',
						payment: cashAmount,
						isPaymentError: false,
						incentiveIds: cashData?.incentiveIds || []
					});
				}
			}

			logger.info('WS-TRACK-MY-OFFER: Converted legacy cache to PaymentObject format', {
				originalFormat: 'legacy',
				convertedCount: paymentObjects.length,
				paymentTypes: paymentObjects.map(p => p.paymentLabel)
			});

			return paymentObjects.length > 0 ? paymentObjects : null;
		} catch (error) {
			logger.error('WS-TRACK-MY-OFFER: Error converting legacy cache format', {
				error: error.message,
				stack: error.stack
			});
			return null;
		}
	};

	const filterBySupportedDealTypes = (payments, dealTypes) => {
        if (!dealTypes || dealTypes.length === 0) {
            return payments;
        }

        const filtered = payments.filter((item) =>
            dealTypes.includes(item?.paymentLabel?.toLowerCase())
        );

        // Fallback to original if filter removes everything
        if (filtered.length === 0 && payments.length > 0) {
            logger.warn('WS-TRACK-MY-OFFER: supportedDealTypes filter removed all payments — falling back to unfiltered', {
                supportedDealTypes: dealTypes,
                availableTypes: payments.map((p) => p?.paymentLabel)
            });
            return payments;
        }

        return filtered;
    };

	const processPaymentObjectEvent = (paymentArray = [], isFromCache = false) => {

		endTimeMarker('payment');
		const elapsedTime = getPerformanceMeasure('payment');

		logger.info('WS-TRACK-MY-OFFER -> uds:PaymentObject:Update received', {
			isFromCache,
			paymentArrayLength: paymentArray?.length || 0,
			paymentTypes: paymentArray?.map((p) => p?.paymentLabel)
		});

		// Track event to New Relic
		if (window.newrelic && window.newrelic.addPageAction) {
			window.newrelic.addPageAction('paymentObjectUpdateReceived', {
				source: isFromCache ? 'cache' : 'fresh',
				paymentCount: paymentArray?.length || 0,
				featureFlag: 'ws-track-my-offer-use-payment-object-event',
				elapsedTime
			});
		}

		// Clear error timeout
		if (errorTimeoutRef.current) {
			logger.info(
				'WS-TRACK-MY-OFFER: Clearing error timeout - PaymentObject data received',
				{
					hadTimeout: true,
					paymentArray: paymentArray ? paymentArray.length : null
				}
			);
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		}

		const filteredPaymentArray = filterBySupportedDealTypes(
            paymentArray,
            supportedDealTypesRef.current
        );

		// Process payment data
		const processedPayments = processPaymentObjectUpdate(filteredPaymentArray, enableIncentives);

		if (!processedPayments) {
			logger.error('WS-TRACK-MY-OFFER: Failed to process PaymentObject data - setting global error state');
			setHasError(true);
			setIsLoading(false);
			return;
		}

		// Handle cache vs fresh data tracking
		if (isFromCache) {
			cachedPaymentDataRef.current = processedPayments;
			hasRenderedCachedRef.current = true;
		} else if (hasRenderedCachedRef.current && cachedPaymentDataRef.current) {
			const mismatchData = comparePaymentData(
				cachedPaymentDataRef.current,
				processedPayments
			);
			if (mismatchData) {
				addPaymentMismatchToNewRelic(mismatchData);
			}
		}

		addPaymentRenderToNewRelic(
			isFromCache ? 'cache' : 'fresh',
			elapsedTime
		);

		if (!isFromCache) {
			addTotalTimeToNewRelic(elapsedTime, 'payment');
		}

		logger.info('WS-TRACK-MY-OFFER: Successfully applied PaymentObject data', {
			processedPaymentsCount: processedPayments.length,
			validPayments: processedPayments.filter(p => !p.hasError).length,
			errorPayments: processedPayments.filter(p => p.hasError).length,
			paymentDetails: processedPayments.map(p => ({
				label: p.paymentLabel,
				hasError: p.hasError,
				payment: p.payment,
				displayValue: p.hasError ? 'N/A' : `$${p.payment.toLocaleString()}`
			}))
		});

		setPaymentsData(processedPayments);
		setUdsPayments(paymentArray);
		setHasReceivedData(true);
		setIsLoading(false);
		setHasError(false);
	};

	// Flush buffered payment data to state
	const flushPendingPayment = () => {
		const pending = pendingPaymentRef.current;
		if (pending) {
			pendingPaymentRef.current = null;
			processPaymentObjectEvent(pending.paymentArray, pending.isFromCache);
		}
	};

	

	// PaymentObject:Update fires multiple times during initial UDS calculation.
	// Buffer first event until the calc-completed signal (calcConfigSlimPreview:completed,
	// or the calcConfigUpdate:completed fallback) or second event arrives.
	const handlePaymentObjectUpdate = (paymentArray, isFromCache = false) => {
		// After initial calc, process immediately — no buffering needed
		if (isInitialCalcDoneRef.current) {
			processPaymentObjectEvent(paymentArray, isFromCache);
			return;
		}

		// Error pages: no valid payments → process immediately
		if (!hasAnyValidPaymentInRawData(paymentArray)) {
			logger.info(
				'WS-TRACK-MY-OFFER: No valid payments in event data — processing immediately (error page)'
			);
			isInitialCalcDoneRef.current = true;
			processPaymentObjectEvent(paymentArray, isFromCache);
			return;
		}

		initialEventCountRef.current += 1;
		
		pendingPaymentRef.current = { paymentArray, isFromCache };

		// Flush now instead of waiting for the calc-completed signal.
		if (initialEventCountRef.current >= 2) {
			logger.info(
				'WS-TRACK-MY-OFFER: 2nd PaymentObject:Update received — flushing settled data'
			);
			isInitialCalcDoneRef.current = true;
			flushPendingPayment();
			return;
		}

		// 1st event with valid data — buffer and wait for either
		// the calc-completed signal or a 2nd event to confirm.
		logger.info(
			'WS-TRACK-MY-OFFER: Buffering 1st PaymentObject:Update (waiting for completed signal or next event)'
		);
	};

	const handlePaymentCalcResponse = (calcResponseArray) => {
        const dealTypes = calcResponseArray?.supportedDealTypes || [];

        logger.info('WS-TRACK-MY-OFFER: uds:PaymentCalcResponse:Update received', {
            supportedDealTypes: dealTypes,
            hasExistingPayments: !!paymentsRef.current
        });

        // Dispatch to Redux store
        if (calcResponseArray?.creditType) {
            dispatch(setPaymentsCalcResponse(calcResponseArray));
        }

        if (dealTypes.length === 0) {
            logger.warn('WS-TRACK-MY-OFFER: PaymentCalcResponse has empty supportedDealTypes — skipping filter');
            return;
        }

        supportedDealTypesRef.current = dealTypes;

        // RE-FILTER: If payments already rendered, re-process with new filter
        if (paymentsRef.current) {
            logger.info(
                'WS-TRACK-MY-OFFER: Re-filtering already rendered payments with supportedDealTypes',
                {
                    supportedDealTypes: dealTypes,
                    currentPayments: paymentsRef.current.map((p) => p?.paymentLabel)
                }
            );

            const filteredPaymentArray = filterBySupportedDealTypes(
                paymentsRef.current,
                dealTypes
            );

            const processedPayments = processPaymentObjectUpdate(
                filteredPaymentArray,
                enableIncentives
            );

            if (processedPayments) {
                setPaymentsData(processedPayments);
            }
        }
    };

	// Check for cached payment data
	useEffect(() => {
		if (isBrowser && flags[CACHE_PRELOAD_FLAG]) {
			try {
				const uuid = extractVehicleUUID(window.location.pathname);
				const paymentData = getCachedPaymentData(uuid);

				if (paymentData) {
					logger.info(
						'WS-TRACK-MY-OFFER: Found cached payment data, converting from legacy format to PaymentObject',
						{ uuid, hasFinanceError: !!paymentData?.isFinanceErrorState, hasLeaseError: !!paymentData?.isLeaseErrorState }
					);

					// Convert legacy cache format to PaymentObject format
					const convertedPaymentArray = convertLegacyCacheToPaymentObject(paymentData);

					if (convertedPaymentArray && convertedPaymentArray.length > 0) {
						// Use converted data with PaymentObject handler
						handlePaymentObjectUpdate(convertedPaymentArray, true);
						logger.info('WS-TRACK-MY-OFFER: Successfully applied converted cache data');
					} else {
						logger.warn('WS-TRACK-MY-OFFER: Skipping cached data - conversion resulted in no valid payments');
					}
				}
			} catch (e) {
				logger.error('WS-TRACK-MY-OFFER: Error processing cached payment data', {
					error: e.message,
					stack: e.stack
				});
				// Ignore errors, continue normally
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags]);

	useEffect(() => {
		if (!enabled || !isReady) {
			return () => {};
		}

		const pubsub = window?.DDC?.pubsub;
		logger.info(
				'WS-TRACK-MY-OFFER: usePaymentData setting up event listeners',
				{
					isReady,
					flags: {
						preloadEnabled: flags[CACHE_PRELOAD_FLAG],
						paymentTimeout
					}
				}
			);
			startTimeMarker('payment');

			// Handle timeout
			errorTimeoutRef.current = setTimeout(() => {
				// Flush buffered data on timeout
				if (pendingPaymentRef.current) {
					logger.info(
						'WS-TRACK-MY-OFFER: Payment timeout reached with buffered data — flushing'
					);
					isInitialCalcDoneRef.current = true;
					flushPendingPayment();
					errorTimeoutRef.current = null;
					return;
				}

				if (flags[CACHE_PRELOAD_FLAG]) {
					// Log warning instead of hiding widget - cached data may still arrive
					logger.warn(
						'WS-TRACK-MY-OFFER: Payment data is taking longer than expected',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
					// Settle loading so the selector can show the unavailable message; late data still overrides
					if (flags[TEST_FUTURE_FLAG] && !paymentsRef.current) {
						setIsLoading(false);
					}
				} else if (!paymentsRef.current) {
					logger.error(
						'WS-TRACK-MY-OFFER: Payment data timeout reached - no data available',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
					setHasError(true);
					setIsLoading(false);
				} else {
					logger.warn('WS-TRACK-MY-OFFER: Payment timeout but cached data exists, continuing');
				}
				// Track timeout for monitoring
				addPaymentTimeoutToNewRelic(paymentTimeout);
				errorTimeoutRef.current = null;
			}, paymentTimeout);

			eventBus.on('uds:PaymentCalcResponse:Update', handlePaymentCalcResponse);
			// Listen for wallet init to get full paymentsInfo
			const handleWalletInit = ({ paymentInfo }) => {
				if (paymentInfo) {
					dispatch(setPaymentsCalcResponse(paymentInfo));
				}
			};
			eventBus.on('wallet:ensurePaymentsInfo:completed', handleWalletInit);
			// Set up PaymentObject:Update event listener
			logger.info('WS-TRACK-MY-OFFER: Using PaymentObject:Update as render source');
			eventBus.on('uds:PaymentObject:Update', handlePaymentObjectUpdate);
			// Handle payment errors from UDS
			const handlePaymentError = (errorData) => {
				logger.error(
					'WS-TRACK-MY-OFFER: Received payment error from UDS-LITE',
					{
						error: errorData?.error,
						context: errorData?.context,
						timestamp: errorData?.timestamp
					}
				);

				// Clear existing timeout
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}

				// Hide loader on error
				setHasError(true);
				setIsLoading(false);

				// Track error
				if (window.newrelic && window.newrelic.addPageAction) {
					window.newrelic.addPageAction('paymentError', {
						error: errorData?.error || 'Unknown error',
						context: errorData?.context || 'payment',
						source: 'uds-lite'
					});
				}
			};

			eventBus.on('uds:paymentUpdate:error', handlePaymentError);

			eventBus.on('uds:calcConfigUpdate:request', (configInfo) => {
				startTimeMarker('payment');
				logger.info(
					'WS-TRACK-MY-OFFER -> uds:calcConfigUpdate:request:',
					configInfo
				);
			});

			const handleConfigCompletedFlush = (configInfo) => {
				if (!isInitialCalcDoneRef.current) {
					logger.info(
						'WS-TRACK-MY-OFFER -> calc config completed:',
						configInfo
					);

					isInitialCalcDoneRef.current = true;
					flushPendingPayment();
				}
			};

			eventBus.on('uds:calcConfigSlimPreview:completed', handleConfigCompletedFlush);
			eventBus.on('uds:calcConfigUpdate:completed', handleConfigCompletedFlush);

			pubsub?.subscribe(
				'ddc-wallet-integration/esntial:paymentError',
				hideOnEsntialPaymentError
			);

			// Backward compatibility for window events
			const handleWindowPaymentError = (event) => {
				if (event.detail) {
					handlePaymentError(event.detail);
				}
			};

			window.addEventListener(
				'uds:payment:error',
				handleWindowPaymentError
			);

			return () => {
				// Clean up all event listeners
				eventBus.off('uds:PaymentCalcResponse:Update', handlePaymentCalcResponse);
				eventBus.off('wallet:ensurePaymentsInfo:completed', handleWalletInit);
				eventBus.off('uds:PaymentObject:Update', handlePaymentObjectUpdate);
				eventBus.off('uds:paymentUpdate:error', handlePaymentError);
				eventBus.off('uds:calcConfigSlimPreview:completed', handleConfigCompletedFlush);
				eventBus.off('uds:calcConfigUpdate:completed', handleConfigCompletedFlush);

				pubsub?.unsubscribe(
					'ddc-wallet-integration/esntial:paymentError',
					hideOnEsntialPaymentError
				);

				window.removeEventListener(
					'uds:payment:error',
					handleWindowPaymentError
				);

				// Clear timeout
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}

				// Clear all refs to prevent memory leaks
				pendingPaymentRef.current = null;
				cachedPaymentDataRef.current = null;
				isInitialCalcDoneRef.current = false;
				initialEventCountRef.current = 0;
				hasRenderedCachedRef.current = false;
				paymentsRef.current = null;
			};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, isReady, eventBus, paymentTimeout, hideOnEsntialPaymentError]);

	// Update paymentsRef for timeout handler
	useEffect(() => {
		paymentsRef.current = udsPayments;
	}, [udsPayments]);

	return [isLoading && !hasReceivedData, hasError, hasReceivedData ? paymentsData : null];
};

export default usePaymentData;
